import React, { useState, useMemo } from 'react'
import clsx from 'clsx'
import { ReactComponent as IconSearch } from 'images/search.svg';
import Button from 'components/shared/Button';
import { Input } from 'components/shared/Input'
import Modal from 'components/shared/modals/Modal';
import styles from './SelectModal.module.scss';
import { useTranslation } from 'react-i18next';

type SelectModalProps = {
  title: string,
  options: any[],
  btnText?: string,
  isOpen?: boolean,
  isSearchable?: boolean,
  multiple?: boolean,
  showIcons?: boolean,
  onClose?: () => void,
  onSelect: (v: any) => void,
  searchFunc?: (v: string) => (v: any) => boolean,
  ItemComponent?: React.FC<{ data: any }>,
  value?: any,
  notFoundMessage?: React.ReactNode,
  notFoundButton?: any,
  noBtn?: boolean,
  maxLength?: number
}

const defaultSearchFunc = (s: string) => (v: any) => ((v.name || '').toLowerCase().includes(s))

function SelectModal({
  title,
  options,
  btnText = 'Select',
  isOpen,
  isSearchable,
  multiple,
  showIcons,
  onClose = () => {},
  searchFunc,
  ItemComponent,
  onSelect,
  value,
  notFoundMessage,
  notFoundButton,
  noBtn,
  maxLength,
}: SelectModalProps) {

  const {t} = useTranslation() as any
  const [selectedOptions, setSelectedOptions] = useState<any[]>((multiple ? value : [value]) || [])
  const [search, setSearch] = useState<string>('')

  const optionsFiltered = useMemo(() => {
    if (!search) return options
    const s = search.toLowerCase()
    return options.filter(searchFunc ? searchFunc(s) : defaultSearchFunc(s))
  }, [options, search])

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value)
  }

  const handleSelection = (v: any) => {
    if (noBtn) {
      onSelect(v)
    }
    if (multiple) {
      setSelectedOptions((old: any[]) => {
        const idx = old.findIndex((oldItem: any) => oldItem?.id === v?.id)

        if (idx > -1) {
          const res = [...old]
          res.splice(idx, 1)
          return res
        }

        if (maxLength && old.length > maxLength - 1) {
          return old
        }

        return [...old, v]
      })
    } else {
      setSelectedOptions((old: any[]) => (
        (old[0]?.id === v?.id) ? [] : [v]
      ))
    }
  }

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className={styles.container}
      onClick={(e: any) => e.stopPropagation()}
    >
      <div className={styles.title}>{t(title)}</div>

      {isSearchable ? (
        <Input
          icon={<IconSearch />}
          className={styles.searchInput}
          value={search}
          onChange={handleSearchChange}
          placeholder="Search on Beautify"
        />
      ) : null}

      {(isSearchable && search && !optionsFiltered?.length) ? (
        <>
          {notFoundMessage}
          {notFoundButton ? (
            <Button
              className={styles.btn}
              onClick={
                () => notFoundButton?.onClick({
                  options,
                  multiple,
                  onClose,
                  value,
                })
              }
              wide
            >
              {t(notFoundButton?.text)}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <div className={clsx(styles.options, 'scroll scroll-y')}>
            {optionsFiltered.map((item) => {
              const Icon = item.icon
              const isActive = selectedOptions?.find((v: any) => (v?.id === item?.id))
              return (
                <div
                  key={item.id}
                  className={styles.option}
                  onClick={() => handleSelection(item)}
                >
                  {ItemComponent ? (
                    <ItemComponent data={item} />
                  ) : (
                    <>
                      {showIcons ? (
                        <div className={styles.icon}>
                          <Icon />
                        </div>
                      ) : null}
                      <span>{item.name}</span>
                    </>
                  )}
                  {noBtn ? null : (
                    <div
                      className={clsx(
                        styles.check,
                        isActive && !multiple && styles.checkActive,
                        isActive && multiple && styles.checkMultipleActive,
                      )}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {noBtn ? null : (
            <Button
              className={styles.btn}
              onClick={() => onSelect(multiple ? selectedOptions : selectedOptions[0])}
              wide
            >
              {t(btnText)}
            </Button>
          )}
        </>
      )}
    </Modal>
  )
}

export default SelectModal
